// frontend/src/pages/Clients.js — Session 18
// + toggle 👁 MDP  + validation belge (TVA/CP/tél/email)  + champs alignés
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Vrai email vs adresse auto-générée c{n}@noreply.mkw-solutions.be
const isRealEmail = (email) =>
  !!(email && !/^c\d+@noreply\.mkw-solutions\.be$/.test(email));

// ── Validation belge ──────────────────────────────────────────────────────────
const validators = {
  email:       v => !v || isRealEmail(v) === false ? '' : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Email invalide',
  telephone:   v => !v || /^(\+32|0)[0-9]{7,9}$/.test(v.replace(/[\s\-\.]/g,'')) ? '' : 'Format: +32 4XX XX XX XX ou 04XX XX XX XX',
  code_postal: v => !v || /^[1-9][0-9]{3}$/.test(v)                   ? '' : 'Code postal belge: 4 chiffres (ex: 1000)',
  numero_tva:  v => !v || /^BE[0-9]{10}$/.test(v.toUpperCase())        ? '' : 'Format TVA belge: BE + 10 chiffres (ex: BE0123456789)',
};

const EMPTY_FORM = {
  nom:'', prenom:'', email:'', telephone:'',
  adresse:'', ville:'', code_postal:'',
  numero_tva:'', type_client:'particulier', actif:true, nom_societe:'',
  mot_de_passe:'', confirmPwd:'',
};

const Field = ({ label, value, onChange, type='text', placeholder='', required=false, error='' }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
    <label style={{ fontSize:'13px', fontWeight:'600', color:'#555' }}>
      {label}{required && <span style={{ color:'#e53e3e' }}> *</span>}
    </label>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      style={{
        padding:'9px 12px', border:`1px solid ${error ? '#e53e3e' : theme.border}`,
        borderRadius:'7px', fontSize:'14px', outline:'none', color:'#2c3e50',
        background: error ? '#fff5f5' : 'white',
      }}
    />
    {error && <span style={{ fontSize:'12px', color:'#e53e3e' }}>{error}</span>}
  </div>
);

const Clients = () => {
  const { token, user } = useAuth();
  const config    = { headers: { Authorization: `Bearer ${token}` } };

  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showModal,   setShowModal]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState('tous');
  const [tempPassword,setTempPassword]= useState(null);
  const [showPwd,     setShowPwd]     = useState(false);
  const [showConfPwd, setShowConfPwd] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/clients`, config);
      setClients(res.data.data || []);
    } catch { setError('Erreur chargement clients'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const set = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    // Validation en temps réel
    if (validators[key]) {
      const err = validators[key](val);
      setFieldErrors(p => ({ ...p, [key]: err }));
    }
    // TVA auto-uppercase
    if (key === 'numero_tva') {
      const up = val.toUpperCase();
      setForm(p => ({ ...p, numero_tva: up }));
      setFieldErrors(p => ({ ...p, numero_tva: validators.numero_tva(up) }));
      return;
    }
  };

  const openCreate = () => {
    setEditTarget(null); setForm(EMPTY_FORM);
    setFieldErrors({}); setError(''); setShowModal(true);
  };

  const openEdit = (client) => {
    setEditTarget(client);
    setForm({
      nom: client.nom||'', prenom: client.prenom||'',
      email: client.email||'', telephone: client.telephone||'',
      adresse: client.adresse||'', ville: client.ville||'',
      code_postal: client.code_postal||'',
      numero_tva: client.numero_tva||'', type_client: client.type_client||'particulier', nom_societe: client.nom_societe||'',
      actif: client.actif !== false,
      mot_de_passe:'', confirmPwd:'',
    });
    setFieldErrors({}); setError(''); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.nom.trim())
      return setError('Nom obligatoire.');

    // Valider tous les champs
    const errs = {};
    Object.keys(validators).forEach(k => {
      const e = validators[k](form[k]);
      if (e) errs[k] = e;
    });
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return setError('Corrigez les erreurs de saisie avant de sauvegarder.');
    }

    if (form.mot_de_passe) {
      if (form.mot_de_passe.length < 6)
        return setError('Le mot de passe doit faire au moins 6 caractères.');
      if (form.mot_de_passe !== form.confirmPwd)
        return setError('Les mots de passe ne correspondent pas.');
    }

    setSaving(true);
    try {
      const payload = {
        nom: form.nom, prenom: form.prenom, email: form.email,
        telephone: form.telephone, adresse: form.adresse,
        ville: form.ville, code_postal: form.code_postal,
        numero_tva: form.numero_tva || null,
        type_client: form.type_client, actif: form.actif, nom_societe: form.nom_societe||null,
        ...(form.mot_de_passe ? { mot_de_passe: form.mot_de_passe } : {}),
      };

      let res;
      if (editTarget) {
        res = await axios.put(`${API_URL}/clients/${editTarget.id_client}`, payload, config);
      } else {
        res = await axios.post(`${API_URL}/clients`, payload, config);
      }

      setShowModal(false);
      fetchClients();

      if (res.data.mot_de_passe_temp) {
        setTempPassword({ email: form.email, password: res.data.mot_de_passe_temp });
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const msg  = err.response?.data?.message || 'Erreur lors de la sauvegarde';
      if (code === 'EMAIL_EXISTE') {
        const ce = err.response.data.client_existant;
        setError(`⚠️ ${msg}\n→ Client existant: ${ce?.prenom} ${ce?.nom} (ID #${ce?.id_client})`);
      } else if (code === 'IDENTITE_EXISTE') {
        const ce = err.response.data.client_existant;
        setError(`⚠️ ${msg}\n→ Utilisez l'email ${ce?.email} pour retrouver ce client.`);
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, nomClient) => {
    if (!window.confirm(`Supprimer le client "${nomClient}" ?\n\nSi ce client a des factures ou devis, il sera désactivé (non supprimé) pour préserver l'intégrité de vos documents.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/clients/${id}`, config);
      if (res.data.desactive) {
        setError('ℹ️ ' + res.data.message);
      }
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleForceDelete = async (id, nomClient) => {
    if (!window.confirm(
      `⚠️ SUPPRESSION DÉFINITIVE\n\nClient : ${nomClient}\n\nToutes les factures, devis, paiements et données associées seront DÉFINITIVEMENT supprimés. Cette action est IRRÉVERSIBLE.\n\nConfirmez-vous ?`
    )) return;
    if (!window.confirm(
      `🚨 DERNIÈRE CONFIRMATION\n\nVous allez supprimer définitivement "${nomClient}" et TOUTES ses données.\n\nCette action ne peut pas être annulée. Continuer ?`
    )) return;
    try {
      await axios.delete(`${API_URL}/clients/${id}/force`, config);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression forcée');
    }
  };

  const filtered = clients.filter(c => {
    const s = search.toLowerCase();
    const match = `${c.nom} ${c.prenom} ${c.email} ${c.telephone||''}`.toLowerCase().includes(s);
    const typeMatch = filterType === 'tous' || c.type_client === filterType;
    return match && typeMatch;
  });

  const isMobile = useIsMobile();
  const nbPro  = clients.filter(c => c.type_client === 'professionnel').length;
  const nbPart = clients.filter(c => c.type_client !== 'professionnel').length;

  if (loading) return <div style={{ padding:'40px', textAlign:'center', color:'#666' }}>⏳ Chargement…</div>;

  return (
    <div style={{ padding: isMobile ? '12px 10px' : '24px', maxWidth:'1100px', margin:'0 auto' }}>

      {/* En-tête */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'10px', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={{ margin:0, fontSize: isMobile ? '20px' : '24px', color:'#2c3e50', fontWeight:'700' }}>👥 Clients</h1>
          <p style={{ margin:'4px 0 0', color:'#666', fontSize:'14px' }}>
            {clients.length} client{clients.length!==1?'s':''} —
            <span style={{ color:'#3498db' }}> {nbPro} pro</span> ·
            <span style={{ color:'#27ae60' }}> {nbPart} particulier{nbPart!==1?'s':''}</span>
          </p>
        </div>
        <button onClick={openCreate} style={{
          background: theme.gradientPrimary, color:'white', border:'none',
          borderRadius:'8px', padding:'10px 18px', fontSize:'14px',
          fontWeight:'600', cursor:'pointer',
        }}>
          ➕ Nouveau client
        </button>
      </div>

      {error && <div style={{ background:'#fff5f5', color:'#e53e3e', padding:'12px 16px', borderRadius:'8px', marginBottom:'16px', fontSize:'14px' }}>⚠️ {error}</div>}

      {/* Barre de recherche + filtre */}
      <div style={{ display:'flex', gap:'12px', marginBottom:'16px', flexWrap:'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Rechercher par nom, email, téléphone…"
          style={{ flex:'1 1 220px', padding:'9px 14px', border:`1px solid ${theme.border}`, borderRadius:'8px', fontSize:'14px', outline:'none' }}
        />
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
          {['tous','particulier','professionnel'].map(t => (
            <button key={t} onClick={() => setFilterType(t)} style={{
              padding:'8px 14px', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px', fontWeight:'600',
              background: filterType===t ? theme.primary : '#f0f0f0',
              color: filterType===t ? 'white' : '#555',
            }}>
              {t==='tous'?'Tous':t==='particulier'?'Particuliers':'Professionnels'}
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px', color:'#999', background:'white', borderRadius:'12px' }}>
            Aucun client trouvé.
          </div>
        )}
        {filtered.map(client => {
          const isPro    = client.type_client === 'professionnel';
          const aCompte  = !!client.id_utilisateur;
          return (
            <div key={client.id_client} style={{
              background:'white', borderRadius:'12px', padding:'16px 20px',
              boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
              borderLeft:`4px solid ${isPro ? '#3498db' : '#27ae60'}`,
              display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px',
              opacity: client.actif === false ? 0.55 : 1,
            }}>
              <div style={{ flex:1, minWidth:'180px' }}>
                <div style={{ fontWeight:'700', fontSize:'15px', color:'#2c3e50' }}>
                  {client.nom_societe
                    ? <><span style={{ color:'#3498db' }}>{client.nom_societe}</span>
                        <span style={{ fontSize:'12px', color:'#888', marginLeft:'6px' }}>({client.prenom} {client.nom})</span></>
                    : <>{client.prenom} {client.nom}</>
                  }
                  {client.actif === false && <span style={{ marginLeft:'8px', fontSize:'11px', color:'#999', background:'#f0f0f0', padding:'2px 7px', borderRadius:'10px' }}>Désactivé</span>}
                </div>
                <div style={{ fontSize:'13px', color:'#666', marginTop:'3px' }}>
                  {isRealEmail(client.email)
                    ? client.email
                    : <span style={{ fontSize:'11px', fontWeight:'600', padding:'2px 8px',
                        borderRadius:'10px', background:'#fff3e0', color:'#e65100',
                        border:'1px solid #ffcc80' }}>Sans email</span>
                  }
                </div>
                {client.telephone && <div style={{ fontSize:'12px', color:'#999' }}>{client.telephone}</div>}
                {isPro && client.numero_tva && <div style={{ fontSize:'11px', color:'#3498db', marginTop:'2px' }}>TVA: {client.numero_tva}</div>}
              </div>
              <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                <span style={{
                  fontSize:'11px', fontWeight:'600', padding:'3px 10px', borderRadius:'12px',
                  background: isPro ? '#ebf5ff' : '#e8f8f0',
                  color: isPro ? '#3498db' : '#27ae60',
                }}>
                  {isPro ? '🏢 Pro' : '👤 Particulier'}
                </span>
                <span style={{
                  fontSize:'11px', fontWeight:'600', padding:'3px 10px', borderRadius:'12px',
                  background: aCompte ? '#e8f8f0' : '#fff5f5',
                  color: aCompte ? '#27ae60' : '#e53e3e',
                }}>
                  {aCompte ? '✅ Compte' : '⚠️ Sans compte'}
                </span>
                <button onClick={() => openEdit(client)} style={{ padding:'7px 14px', background:'#f0f4f8', color:'#4f6d89', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>
                  ✏️ Modifier
                </button>
                <button onClick={() => handleDelete(client.id_client, `${client.prenom} ${client.nom}`)} style={{ padding:'7px 12px', background:'#fff5f5', color:'#e53e3e', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px' }}>
                  🗑️
                </button>
                {user?.is_super_admin && (
                  <button
                    onClick={() => handleForceDelete(client.id_client, `${client.prenom} ${client.nom}`)}
                    title="Suppression définitive (superAdmin)"
                    style={{ padding:'7px 10px', background:'#2d1515', color:'#ff6b6b', border:'1px solid #c0392b', borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:'700', whiteSpace:'nowrap' }}
                  >
                    ☠️ Forcer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Affichage mot de passe temporaire */}
      {tempPassword && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001 }}>
          <div style={{ background:'white', borderRadius:'14px', padding:'28px', maxWidth:'400px', width:'96%', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin:'0 0 16px', color:'#27ae60', fontSize:'18px' }}>✅ Compte créé avec succès</h2>
            <p style={{ fontSize:'14px', color:'#555', margin:'0 0 16px' }}>
              Un email avec les identifiants a été envoyé au client.<br/>
              Vous pouvez également noter les identifiants ci-dessous :
            </p>
            <div style={{ background:'#f0f4f8', borderRadius:'8px', padding:'16px', marginBottom:'16px', fontFamily:'monospace' }}>
              <div style={{ fontSize:'13px', color:'#666', marginBottom:'6px' }}>EMAIL</div>
              <div style={{ fontWeight:'bold', color:'#2c3e50', marginBottom:'14px' }}>{tempPassword.email}</div>
              <div style={{ fontSize:'13px', color:'#666', marginBottom:'6px' }}>MOT DE PASSE TEMPORAIRE</div>
              <div style={{ fontWeight:'bold', fontSize:'20px', color:'#4f6d89', letterSpacing:'2px' }}>{tempPassword.password}</div>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => { navigator.clipboard?.writeText(`Email: ${tempPassword.email}\nMot de passe: ${tempPassword.password}`); }} style={{ flex:1, padding:'10px', background:'#f0f4f8', color:'#4f6d89', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
                📋 Copier
              </button>
              <button onClick={() => setTempPassword(null)} style={{ flex:1, padding:'10px', background:theme.gradientPrimary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
                ✅ Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal création/édition */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'16px' }}
          onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div style={{ background:'white', borderRadius:'14px', padding: isMobile ? '18px 14px' : '28px', maxWidth:'540px', width:'100%', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin:'0 0 20px', color:theme.primary, fontSize:'18px' }}>
              {editTarget ? '✏️ Modifier le client' : '➕ Nouveau client'}
            </h2>

            {error && <div style={{ background:'#fff5f5', color:'#e53e3e', padding:'10px 14px', borderRadius:'8px', marginBottom:'14px', fontSize:'13px' }}>⚠️ {error}</div>}

            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

              {/* Type client */}
              <div>
                <label style={{ fontSize:'13px', fontWeight:'600', color:'#555', display:'block', marginBottom:'6px' }}>Type de client</label>
                <div style={{ display:'flex', gap:'8px' }}>
                  {[{v:'particulier',l:'👤 Particulier'},{v:'professionnel',l:'🏢 Professionnel'}].map(t => (
                    <button key={t.v} type="button" onClick={() => set('type_client',t.v)} style={{
                      flex:1, padding:'8px', border:`2px solid ${form.type_client===t.v?theme.primary:'#e0e0e0'}`,
                      borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px',
                      background: form.type_client===t.v ? theme.primaryBg || '#edf2fb' : 'white',
                      color: form.type_client===t.v ? theme.primary : '#666',
                    }}>{t.l}</button>
                  ))}
                </div>
              </div>

              {/* Nom / Prénom */}
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'12px' }}>
                <Field label="Nom" value={form.nom} onChange={v=>set('nom',v)} required placeholder="Dupont" />
                <Field label="Prénom" value={form.prenom} onChange={v=>set('prenom',v)} placeholder="Jean" />
              </div>

              <Field label="Email" type="email" value={form.email} onChange={v=>set('email',v)}
                placeholder="jean@exemple.be (optionnel)" error={fieldErrors.email||''} />
              {editTarget && isRealEmail(editTarget.email) && (
                <div style={{ fontSize:'12px', color:'#e67e22', background:'#fef9f0', padding:'8px 12px', borderRadius:'6px', marginTop:'-8px' }}>
                  ⚠️ Modifier l'email enverra automatiquement les nouveaux identifiants au client et réinitialisera son mot de passe.
                </div>
              )}

              <Field label="Téléphone" type="tel" value={form.telephone} onChange={v=>set('telephone',v)}
                placeholder="+32 4XX XX XX XX" error={fieldErrors.telephone||''} />

              <Field label="Adresse" value={form.adresse} onChange={v=>set('adresse',v)} placeholder="Rue et numéro" />

              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'12px' }}>
                <Field label="Ville" value={form.ville} onChange={v=>set('ville',v)} placeholder="Bruxelles" />
                <Field label="Code postal" value={form.code_postal} onChange={v=>set('code_postal',v)}
                  placeholder="1000" error={fieldErrors.code_postal||''} />
              </div>

              {form.type_client === 'professionnel' && (
                <>
                  <Field label="Nom de la société *" value={form.nom_societe} onChange={v=>set('nom_societe',v)}
                    placeholder="ex: BeApart NV, MKW Solutions SA..." />
                  <Field label="N° TVA (BE...)" value={form.numero_tva} onChange={v=>set('numero_tva',v)}
                    placeholder="BE0123456789" error={fieldErrors.numero_tva||''} />
                  {editTarget?.numero_tva && (
                    <div style={{ fontSize:'12px', color:'#4f6d89', background:'#f0f4f8', padding:'8px 12px', borderRadius:'6px', marginTop:'-8px' }}>
                      ℹ️ Une correction du N° TVA enverra un email de notification au client.
                    </div>
                  )}
                </>
              )}

              {/* Mot de passe avec toggle 👁 */}
              <div style={{ borderTop:`1px solid ${theme.border}`, paddingTop:'14px', marginTop:'4px' }}>
                <label style={{ fontSize:'13px', fontWeight:'700', color:'#555', display:'block', marginBottom:'10px' }}>
                  {editTarget ? '🔑 Nouveau mot de passe (laisser vide pour ne pas changer)' : '🔑 Mot de passe temporaire (généré si vide)'}
                </label>
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {[
                    { key:'mot_de_passe', label:'Mot de passe', show:showPwd, toggle:()=>setShowPwd(v=>!v), ph:'Minimum 6 caractères' },
                    { key:'confirmPwd', label:'Confirmer', show:showConfPwd, toggle:()=>setShowConfPwd(v=>!v), ph:'Répétez le mot de passe' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ fontSize:'12px', color:'#666', display:'block', marginBottom:'4px' }}>{f.label}</label>
                      <div style={{ position:'relative' }}>
                        <input
                          type={f.show ? 'text' : 'password'}
                          value={form[f.key]}
                          onChange={e => set(f.key, e.target.value)}
                          placeholder={f.ph}
                          style={{ width:'100%', padding:'9px 40px 9px 12px', border:`1px solid ${theme.border}`, borderRadius:'7px', fontSize:'14px', outline:'none', boxSizing:'border-box' }}
                        />
                        <button type="button" onClick={f.toggle} style={{
                          position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)',
                          background:'none', border:'none', cursor:'pointer', fontSize:'16px', padding:'2px',
                        }}>
                          {f.show ? '🙈' : '👁'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actif/Inactif (édition uniquement) */}
              {editTarget && (
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <input type="checkbox" id="actif" checked={form.actif}
                    onChange={e => set('actif', e.target.checked)}
                    style={{ width:'16px', height:'16px', cursor:'pointer' }} />
                  <label htmlFor="actif" style={{ fontSize:'14px', cursor:'pointer', color:'#555' }}>
                    Compte actif
                  </label>
                </div>
              )}

              <div style={{ display:'flex', gap:'12px', marginTop:'8px' }}>
                <button type="submit" disabled={saving} style={{
                  flex:1, padding:'12px', background: saving ? '#ccc' : theme.gradientPrimary,
                  color:'white', border:'none', borderRadius:'8px', cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight:'600', fontSize:'15px',
                }}>
                  {saving ? '⏳ Enregistrement…' : '💾 Sauvegarder'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} style={{
                  flex:1, padding:'12px', background:'#95a5a6', color:'white',
                  border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'15px',
                }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
